/**
 * MBA Lab — pure state + view model for the MBA Program Shortlist Builder.
 * No DOM, no storage, no fetch, no analytics: program-shortlist.js owns all of
 * that and consumes buildComparisonViewModel() from here, matching
 * charter-model.js / interview-model.js's split.
 *
 * THE LOAD-BEARING RULE OF THIS FILE: persisted state stores SLUGS AND USER
 * JUDGMENTS ONLY. Not a school name, not a tuition figure, not an earnings
 * number - every fact is re-resolved from the live dataset on each render by
 * buildComparisonViewModel(). A cached fact is a fact that can silently go
 * stale and then be shown as current, which is the one thing this codebase's
 * data honesty rules do not tolerate. `programSlug` is stored because it is a
 * user's choice of which program to compare, not a fact about the school.
 *
 * The second rule: NO COMPOSITE SCORE. There is no weighting, no ranking, no
 * "best fit" - deliberately. Five plain-language judgments about five
 * incommensurable things cannot be honestly collapsed into one number, and a
 * number would carry far more authority than the input deserves. The only
 * ordering is the user's own tier, in TIERS order.
 *
 * Third rule: absent data renders as an explicit statement of absence
 * ("Not reported", "Not listed in our data"), never as a zero, a dash, or -
 * worse - a confident "No". A derived tuition total is always visibly flagged
 * estimated.
 */

import {
  CRITERIA,
  TIERS,
  DEFAULT_TIER,
  isValidCriterionKey,
  isValidCriterionValue,
  criterionValueLabel,
  isValidTier,
  tierLabel,
} from './shortlist-criteria.js';

export const SCHEMA_NAME = 'mbalab.mba-program-shortlist';
export const SCHEMA_VERSION = 1;

/**
 * Six. Not a technical limit - a comparison you can actually hold in your head
 * and finish. Past six, people stop comparing and start collecting.
 */
export const MAX_SCHOOLS = 6;

/** The ROI calculator's path, with no query string. Exported because the
 * analytics call for a cross-link click must send THIS, never the prefilled
 * href (which carries a school slug). */
export const ROI_TOOL_PATH = '/tools/mba-roi-calculator';

export const NOT_REPORTED = 'Not reported';
export const AACSB_NOT_LISTED = 'Not listed in our data';
export const AACSB_LISTED = 'AACSB-accredited';
export const NO_PROGRAM_SELECTED = 'Choose a program';
export const ESTIMATED_LABEL = 'Estimated';

/** Fixed row order for the comparison. Both the desktop table (one <tr> per
 * entry) and the mobile cards (one <dt>/<dd> per entry) walk this same list in
 * this same order, so the two renderings can never disagree about what is
 * shown or what it is called. */
export const FACT_ROWS = Object.freeze([
  { key: 'location', label: 'Location' },
  { key: 'aacsb', label: 'AACSB accreditation' },
  { key: 'program', label: 'Program compared' },
  { key: 'format', label: 'Format' },
  { key: 'length', label: 'Program length' },
  { key: 'tuition', label: 'Total tuition' },
  { key: 'earnings1Yr', label: 'Median earnings, 1 year out' },
  { key: 'earnings4Yr', label: 'Median earnings, 4 years out' },
  { key: 'debt', label: 'Median debt at graduation' },
]);

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/* -------------------------------------------------------------------------- */
/* Formatting helpers - every one of these returns a statement of absence      */
/* rather than an empty string, a zero, or a dash.                             */
/* -------------------------------------------------------------------------- */

function money(value) {
  return Number.isFinite(value) ? currencyFormatter.format(value) : null;
}

/** Cohort size next to a median. Shown because a median of 300 graduates and a
 * median of 12 deserve very different confidence, and hiding the difference
 * would be spin by omission (same reasoning as CollegeOutcomes). */
function cohortText(count) {
  if (!Number.isInteger(count) || count <= 0) return null;
  return count === 1 ? '1 graduate' : `${count} graduates`;
}

function formatVerifiedOn(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(date.getTime()) ? null : dateFormatter.format(date);
}

function outcomeFact(key, label, value, cohort) {
  const text = money(value);
  return {
    key,
    label,
    text: text || NOT_REPORTED,
    reported: text != null,
    cohortText: text ? cohortText(cohort) : null,
  };
}

/**
 * Every field on the wire is optional except slug/name/stateCode/city/aacsb/
 * directoryUrl/programs - the provider serializes with
 * JsonIgnoreCondition.WhenWritingNull, so a null column is an ABSENT KEY, not
 * a null value. Read defensively: `school.debt` is `undefined` far more often
 * than it is `null`.
 */
function int(value) {
  return Number.isFinite(value) ? value : null;
}

/* -------------------------------------------------------------------------- */
/* State                                                                       */
/* -------------------------------------------------------------------------- */

export function createInitialState() {
  return { schema: SCHEMA_NAME, version: SCHEMA_VERSION, savedAt: null, schools: [] };
}

function sanitizeRatings(raw) {
  const ratings = {};
  if (!raw || typeof raw !== 'object') return ratings;
  for (const criterion of CRITERIA) {
    const value = raw[criterion.key];
    if (isValidCriterionValue(criterion.key, value)) ratings[criterion.key] = value;
  }
  return ratings;
}

/** A slug we would be willing to put in a URL or send to the ROI calculator.
 * Nothing here trusts the shape of a slug that arrived from localStorage or a
 * query string. */
export function isSlugLike(value) {
  return typeof value === 'string' && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) && value.length <= 120;
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const rawSchools = Array.isArray(r.schools) ? r.schools : [];
  const seen = new Set();
  const schools = [];

  for (const entry of rawSchools) {
    if (schools.length >= MAX_SCHOOLS) break;
    if (!entry || typeof entry !== 'object') continue;
    if (!isSlugLike(entry.slug) || seen.has(entry.slug)) continue;
    seen.add(entry.slug);
    schools.push({
      slug: entry.slug,
      tier: isValidTier(entry.tier) ? entry.tier : DEFAULT_TIER,
      programSlug: isSlugLike(entry.programSlug) ? entry.programSlug : null,
      ratings: sanitizeRatings(entry.ratings),
    });
  }

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    schools,
  };
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - same note as charter-model.js /
  // interview-model.js. Proves the mechanism works before it is ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/**
 * @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null}}
 * Accepts either a JSON string or an already-parsed object (core.js's store()
 * hands back the latter).
 */
export function parseSavedState(raw) {
  let parsed;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null };
    }
  } else {
    parsed = raw;
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    Array.isArray(parsed) ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    return { status: 'malformed', state: null };
  }
  if (parsed.version > SCHEMA_VERSION) {
    // A newer build wrote this. Refusing beats silently discarding the fields
    // this build does not understand and writing the truncated version back.
    return { status: 'unsupported-newer', state: null };
  }

  const { state, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(state) };
}

export function serializeForSave(state) {
  return JSON.stringify(state);
}

export function hasSchool(state, slug) {
  return state.schools.some((s) => s.slug === slug);
}

export function isFull(state) {
  return state.schools.length >= MAX_SCHOOLS;
}

export function schoolCount(state) {
  return state.schools.length;
}

/** No-op (not an error) when the slug is malformed, already present, or the
 * cap is reached - the UI disables the control at the cap, so this only guards
 * a direct or racing call. */
export function addSchool(state, slug, tier = DEFAULT_TIER) {
  if (!isSlugLike(slug) || hasSchool(state, slug) || isFull(state)) return state;
  return {
    ...state,
    schools: [
      ...state.schools,
      { slug, tier: isValidTier(tier) ? tier : DEFAULT_TIER, programSlug: null, ratings: {} },
    ],
  };
}

export function removeSchool(state, slug) {
  if (!hasSchool(state, slug)) return state;
  return { ...state, schools: state.schools.filter((s) => s.slug !== slug) };
}

export function setTier(state, slug, tier) {
  if (!isValidTier(tier) || !hasSchool(state, slug)) return state;
  return {
    ...state,
    schools: state.schools.map((s) => (s.slug === slug ? { ...s, tier } : s)),
  };
}

/** `value === null` clears the rating for that criterion. An unrecognized
 * criterion key or value is ignored rather than stored. */
export function setRating(state, slug, criterionKey, value) {
  if (!isValidCriterionKey(criterionKey) || !hasSchool(state, slug)) return state;
  if (value !== null && !isValidCriterionValue(criterionKey, value)) return state;

  return {
    ...state,
    schools: state.schools.map((s) => {
      if (s.slug !== slug) return s;
      const ratings = { ...s.ratings };
      if (value === null) delete ratings[criterionKey];
      else ratings[criterionKey] = value;
      return { ...s, ratings };
    }),
  };
}

/** `programSlug === null` clears the choice. Validity against the school's own
 * program list is checked at render time by buildComparisonViewModel, not
 * here - the dataset is not this module's to hold. */
export function setProgram(state, slug, programSlug) {
  if (!hasSchool(state, slug)) return state;
  if (programSlug !== null && !isSlugLike(programSlug)) return state;
  return {
    ...state,
    schools: state.schools.map((s) => (s.slug === slug ? { ...s, programSlug } : s)),
  };
}

export function ratedCount(state) {
  return state.schools.filter((s) => Object.keys(s.ratings).length > 0).length;
}

export function countsFor(state) {
  return {
    total: state.schools.length,
    shortlist: state.schools.filter((s) => s.tier === 'shortlist').length,
    maybe: state.schools.filter((s) => s.tier === 'maybe').length,
    ruledOut: state.schools.filter((s) => s.tier === 'ruled-out').length,
    rated: ratedCount(state),
  };
}

/** Counts taken from the rows actually rendered, not from raw state. These are
 * what the tool_result event reports, so they have to describe what the person
 * is looking at - a saved slug the dataset no longer has is not on screen and
 * must not be counted. */
function countsForRows(rows) {
  return {
    total: rows.length,
    shortlist: rows.filter((r) => r.tier === 'shortlist').length,
    maybe: rows.filter((r) => r.tier === 'maybe').length,
    ruledOut: rows.filter((r) => r.tier === 'ruled-out').length,
    rated: rows.filter((r) => r.ratings.some((rating) => rating.value !== null)).length,
  };
}

/* -------------------------------------------------------------------------- */
/* Deep link                                                                   */
/* -------------------------------------------------------------------------- */

/** Splits a `?schools=a,b,c` value. Read client-side only - the controller
 * never sees this and the canonical URL never carries it. */
export function parseSchoolsParam(value) {
  if (typeof value !== 'string' || !value) return [];
  return value
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * Adds URL-supplied schools to whatever is already there. Deliberately ADDITIVE:
 * a shared link must never wipe the tiers and ratings someone has already
 * entered, so an already-present slug is skipped rather than reset.
 *
 * @param {object} state
 * @param {string[]} slugsFromUrl
 * @param {Set<string>|null} [knownSlugs] Slugs actually present in the loaded
 *   dataset. When supplied, anything else is dropped here. When omitted (the
 *   dataset has not loaded yet), only malformed slugs are dropped and the
 *   caller MUST follow with reconcileWithDataset() once it has, which drops the
 *   rest.
 */
export function mergeUrlSchools(state, slugsFromUrl, knownSlugs = null) {
  if (!Array.isArray(slugsFromUrl) || slugsFromUrl.length === 0) return state;

  let next = state;
  for (const slug of slugsFromUrl) {
    if (!isSlugLike(slug)) continue;
    if (knownSlugs && !knownSlugs.has(slug)) continue;
    if (isFull(next)) break;
    next = addSchool(next, slug);
  }
  return next;
}

/* -------------------------------------------------------------------------- */
/* Dataset reconciliation                                                      */
/* -------------------------------------------------------------------------- */

function indexDataset(dataset) {
  const schools = dataset && Array.isArray(dataset.schools) ? dataset.schools : [];
  return new Map(schools.filter((s) => s && typeof s.slug === 'string').map((s) => [s.slug, s]));
}

/**
 * Drops saved schools the dataset no longer contains (a school leaving the
 * directory, a slug being retired) and reports it. Silence here would be the
 * bad outcome: a row simply vanishing between visits with no explanation reads
 * as a bug, and worse, hides that the underlying data changed.
 *
 * @returns {{state: object, droppedSlugs: string[], note: string|null}}
 */
export function reconcileWithDataset(state, dataset) {
  const byslug = indexDataset(dataset);
  const droppedSlugs = state.schools.filter((s) => !byslug.has(s.slug)).map((s) => s.slug);

  if (droppedSlugs.length === 0) return { state, droppedSlugs, note: null };

  const kept = state.schools.filter((s) => byslug.has(s.slug));
  const noun = droppedSlugs.length === 1 ? 'school was' : 'schools were';
  const pronoun = droppedSlugs.length === 1 ? 'it is' : 'they are';
  const note =
    `${droppedSlugs.length} saved ${noun} removed from this comparison because ` +
    `${pronoun} no longer in MBA Lab's directory data: ${droppedSlugs.join(', ')}.`;

  return { state: { ...state, schools: kept }, droppedSlugs, note };
}

/* -------------------------------------------------------------------------- */
/* Comparison view model                                                       */
/* -------------------------------------------------------------------------- */

/** The provider builds directoryUrl as `/mba-programs-{schoolSlug}-{stateSlug}`
 * (SchoolsToolDataProvider.BuildJson). Both halves contain hyphens, so the only
 * safe split is to strip the known school slug off the front and take what is
 * left - never a lastIndexOf('-'), which would chop "new-york" to "york".
 * Returns null when the URL does not have that exact shape, and the caller then
 * omits `state` from the ROI link entirely rather than guessing one. */
export function stateSlugFromDirectoryUrl(directoryUrl, schoolSlug) {
  if (typeof directoryUrl !== 'string' || typeof schoolSlug !== 'string') return null;
  const prefix = `/mba-programs-${schoolSlug}-`;
  if (!directoryUrl.startsWith(prefix)) return null;
  const rest = directoryUrl.slice(prefix.length);
  return isSlugLike(rest) ? rest : null;
}

/**
 * Prefill link into the ROI calculator. Query shape verified against
 * ToolsController.RoiCalculator(school, state, program, format) and
 * RoiToolService.ResolvePrefill: `school` is an exact college slug, `state` an
 * exact state slug (or 2-letter code), `program` an exact program slug within
 * that school. `state` is omitted when it cannot be derived - RoiToolService
 * then resolves the school globally, which succeeds as long as the slug is
 * unique across states.
 */
export function roiHrefFor({ slug, stateSlug, programSlug }) {
  const parts = [`school=${encodeURIComponent(slug)}`];
  if (stateSlug) parts.push(`state=${encodeURIComponent(stateSlug)}`);
  if (programSlug) parts.push(`program=${encodeURIComponent(programSlug)}`);
  return `${ROI_TOOL_PATH}?${parts.join('&')}`;
}

function programViewFor(program) {
  return {
    slug: program.slug,
    name: program.name,
    format: typeof program.format === 'string' ? program.format : null,
  };
}

/**
 * Resolves which program a row is showing. An explicit user choice wins. With
 * no choice and exactly one program on record, that program is shown - the
 * dataset only carries schools with at least one researched program, and
 * showing the single thing we have is not a guess. With more than one, nothing
 * is picked: the row says so instead of quietly choosing the cheapest.
 */
function resolveProgram(school, programSlug) {
  const programs = Array.isArray(school.programs) ? school.programs.filter((p) => p && typeof p.slug === 'string') : [];
  if (programSlug) {
    const chosen = programs.find((p) => p.slug === programSlug);
    if (chosen) return { program: chosen, programs, explicit: true };
  }
  if (programs.length === 1) return { program: programs[0], programs, explicit: false };
  return { program: null, programs, explicit: false };
}

/**
 * Total tuition for one program. A real published total always wins. Only when
 * there is none does this derive one from the per-credit rate, and a derived
 * figure is ALWAYS flagged estimated - both as a boolean the renderers turn
 * into a visible badge and as prose in `note`, because a derived total that
 * reads like a published one is the single most misleading thing this tool
 * could show.
 */
function tuitionFact(program) {
  const base = { key: 'tuition', label: 'Total tuition', estimated: false, note: null, verifiedText: null, sourceUrl: null };
  if (!program) return { ...base, text: NO_PROGRAM_SELECTED, reported: false };

  const verifiedText = formatVerifiedOn(program.verifiedOn);
  const sourceUrl = typeof program.sourceUrl === 'string' ? program.sourceUrl : null;
  const perCredit = int(program.tuitionPerCredit);
  const perCreditOutOfState = int(program.tuitionPerCreditOutOfState);
  const creditHours = int(program.creditHours);
  const total = int(program.totalTuition);

  const notes = [];
  if (perCreditOutOfState != null) {
    notes.push(`Out-of-state rate on record: ${money(perCreditOutOfState)} per credit hour.`);
  }

  if (total != null) {
    return {
      ...base,
      text: money(total),
      reported: true,
      note: notes.length ? notes.join(' ') : null,
      verifiedText,
      sourceUrl,
    };
  }

  if (perCredit != null && creditHours != null && creditHours > 0) {
    notes.unshift(
      `${ESTIMATED_LABEL} from ${money(perCredit)} per credit hour x ${creditHours} credit hours. ` +
        'The school does not publish a program total we could verify.'
    );
    return {
      ...base,
      text: money(perCredit * creditHours),
      reported: true,
      estimated: true,
      estimatedLabel: ESTIMATED_LABEL,
      note: notes.join(' '),
      verifiedText,
      sourceUrl,
    };
  }

  return {
    ...base,
    text: NOT_REPORTED,
    reported: false,
    note: notes.length ? notes.join(' ') : null,
    verifiedText,
    sourceUrl,
  };
}

function factsFor(school, program) {
  const lengthMonths = int(program?.lengthMonths);

  return [
    {
      key: 'location',
      label: 'Location',
      text: [school.city, school.stateCode].filter(Boolean).join(', ') || NOT_REPORTED,
      reported: Boolean(school.city || school.stateCode),
    },
    {
      key: 'aacsb',
      label: 'AACSB accreditation',
      // `aacsb` is a non-nullable bool on the wire and is always present. False
      // means OUR DATA does not list it - it does not mean the school is
      // unaccredited, and rendering it as "No" would be a claim we cannot back.
      text: school.aacsb === true ? AACSB_LISTED : AACSB_NOT_LISTED,
      reported: school.aacsb === true,
    },
    {
      key: 'program',
      label: 'Program compared',
      text: program ? program.name : NO_PROGRAM_SELECTED,
      reported: Boolean(program),
    },
    {
      key: 'format',
      label: 'Format',
      text: program?.format || (program ? NOT_REPORTED : NO_PROGRAM_SELECTED),
      reported: Boolean(program?.format),
    },
    {
      key: 'length',
      label: 'Program length',
      text: lengthMonths != null ? `${lengthMonths} months` : (program ? NOT_REPORTED : NO_PROGRAM_SELECTED),
      reported: lengthMonths != null,
    },
    tuitionFact(program),
    outcomeFact('earnings1Yr', 'Median earnings, 1 year out', int(school.earnings1Yr), int(school.earnings1YrCohort)),
    outcomeFact('earnings4Yr', 'Median earnings, 4 years out', int(school.earnings4Yr), int(school.earnings4YrCohort)),
    outcomeFact('debt', 'Median debt at graduation', int(school.debt), int(school.debtCohort)),
  ];
}

/**
 * THE one function both renderings consume. The desktop <table> and the
 * below-768px stacked cards each walk `rows` and, inside each row, `facts` in
 * FACT_ROWS order - so the two can never drift into showing different numbers,
 * different labels, or a different notion of what is missing.
 *
 * Rows come back in TIERS order (shortlist, maybe, ruled out), and within a
 * tier in the order the user added them. That is the whole ordering story:
 * no score, no rank, no sort-by-tuition.
 */
export function buildComparisonViewModel(state, dataset) {
  const bySlug = indexDataset(dataset);
  const rows = [];

  for (const tier of TIERS) {
    for (const saved of state.schools) {
      if (saved.tier !== tier.key) continue;
      const school = bySlug.get(saved.slug);
      // Unknown slug: skipped rather than rendered as an empty row.
      // reconcileWithDataset() is what tells the user it happened.
      if (!school) continue;

      const { program, programs, explicit } = resolveProgram(school, saved.programSlug);
      const stateSlug = stateSlugFromDirectoryUrl(school.directoryUrl, school.slug);

      rows.push({
        slug: school.slug,
        name: school.name,
        tier: tier.key,
        tierLabel: tier.label,
        facts: factsFor(school, program),
        programs: programs.map(programViewFor),
        selectedProgramSlug: program ? program.slug : null,
        programChoiceIsExplicit: explicit,
        directoryUrl: typeof school.directoryUrl === 'string' ? school.directoryUrl : null,
        // Only when present - the provider omits the key entirely for schools
        // with no net price calculator on record.
        npcUrl: typeof school.npcUrl === 'string' && school.npcUrl ? school.npcUrl : null,
        roiHref: roiHrefFor({ slug: school.slug, stateSlug, programSlug: program ? program.slug : null }),
        ratings: CRITERIA.map((criterion) => {
          const value = saved.ratings[criterion.key] || null;
          return {
            key: criterion.key,
            label: criterion.label,
            value,
            valueLabel: value ? criterionValueLabel(criterion.key, value) : null,
          };
        }),
      });
    }
  }

  return {
    isEmpty: rows.length === 0,
    criteria: CRITERIA,
    tiers: TIERS,
    factRows: FACT_ROWS,
    rows,
    counts: countsForRows(rows),
    outcomesRelease: typeof dataset?.release === 'string' ? dataset.release : null,
  };
}

export { CRITERIA, TIERS, DEFAULT_TIER, tierLabel, isValidTier };
